// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { StatusBanner } from '@loa/ui-components';
import { formatDateTime } from '@loa/theme';
import type { NudgeRateLimitWarningProps } from '../../types/nudge';
import { canNudge } from '../../types/nudge';

/**
 * NudgeRateLimitWarning — compact rate-limit hint above the composer.
 *
 * <p>Soft warnings (RECENTLY_SENT, approaching-limit codes) render as a
 * single-row data table under a small "Recent Nudge Detected" heading,
 * followed by the advisor disclaimer. Hard blocks (BLOCKED code) still
 * render the larger StatusBanner with the warning message.
 */
export const NudgeRateLimitWarning = ({ rateLimitStatus }: NudgeRateLimitWarningProps) => {
  if (rateLimitStatus.warningCode === 'NONE') {
    return null;
  }

  if (!canNudge(rateLimitStatus)) {
    return (
      <StatusBanner
        variant="error"
        visible
        position="inline"
        dismissible={false}
        message={
          <>
            <strong>Nudge Not Available</strong>
            <p style={{ margin: '0.5rem 0 0' }}>
              {rateLimitStatus.warningMessage ||
                'Rate limit exceeded. Please wait before sending another nudge.'}
            </p>
            {rateLimitStatus.lastNudgeAt && (
              <p style={{ margin: '0.25rem 0 0' }}>
                <strong>Last nudge sent:</strong> {formatDateTime(rateLimitStatus.lastNudgeAt)}
              </p>
            )}
            <p style={{ margin: '0.25rem 0 0' }}>
              <strong>Today&apos;s nudges for this policy:</strong>{' '}
              {rateLimitStatus.nudgesForPolicyToday} /{' '}
              {rateLimitStatus.thresholds.maxPerPolicyPerDay}
            </p>
          </>
        }
      />
    );
  }

  const getHeading = () => {
    switch (rateLimitStatus.warningCode) {
      case 'RECENTLY_SENT':
        return 'Recent Nudge Detected';
      case 'DAILY_LIMIT_APPROACHING':
        return 'Approaching Daily Limit';
      case 'PROVIDER_LIMIT_APPROACHING':
        return 'Approaching Provider Limit';
      default:
        return 'Rate Limit Warning';
    }
  };

  const lastNudgeLabel = rateLimitStatus.lastNudgeAt
    ? formatDateTime(rateLimitStatus.lastNudgeAt)
    : '—';

  return (
    <div className="bg-[#FFF8E6] border border-[#F3D583] rounded-lg px-4 py-3">
      <p className="!m-0 text-sm font-semibold text-black">{getHeading()}</p>
      <table className="w-full text-sm mt-2 mb-2">
        <thead>
          <tr className="text-grey-02 text-xs">
            <th className="text-left font-medium pr-4 py-1 whitespace-nowrap">Last nudge</th>
            <th className="text-left font-medium pr-4 py-1 whitespace-nowrap">Policy nudges today</th>
            <th className="text-left font-medium pr-4 py-1 whitespace-nowrap">Provider nudges this week</th>
            <th className="text-left font-medium pr-4 py-1 whitespace-nowrap">Minimum time between nudges</th>
          </tr>
        </thead>
        <tbody>
          <tr className="text-grey-01">
            <td className="pr-4 py-0.5 whitespace-nowrap">{lastNudgeLabel}</td>
            <td className="pr-4 py-0.5 whitespace-nowrap">
              {rateLimitStatus.nudgesForPolicyToday} / {rateLimitStatus.thresholds.maxPerPolicyPerDay}
            </td>
            <td className="pr-4 py-0.5 whitespace-nowrap">
              {rateLimitStatus.nudgesForProviderThisWeek} / {rateLimitStatus.thresholds.maxPerProviderPerWeek}
            </td>
            <td className="pr-4 py-0.5 whitespace-nowrap">
              {rateLimitStatus.thresholds.minHoursBetweenNudges}h
            </td>
          </tr>
        </tbody>
      </table>
      <p className="!m-0 text-xs text-grey-02 italic">
        You can still send a nudge, but consider whether the provider needs more time to process the original request.
      </p>
    </div>
  );
};

export default NudgeRateLimitWarning;
